// Hover-skiff kinematics, shared verbatim by the server tick and the
// rider's client prediction. The input stays the ordinary movement intent
// (a world-space direction): the skiff chases it — throttle comes from how
// aligned the stick is with the nose, steering authority tapers with
// speed, and opposing the nose brakes harder than the throttle accelerates.
// Pure functions; height never appears (hover y derives from heightAt at
// render time).

import { SKIFF_BRAKE } from "./constants";
import { SKIFF_WEAR_TUNING, type SkiffTuning, skiffAccel, skiffTopSpeed } from "./equipment";
import { wrapAngle } from "./movement";
import type { InputMessage } from "./protocol";
import type { AreaPlan } from "./world-plan";

/** Yaw rate when crawling, rad/s — tight enough to park. */
export const SKIFF_YAW_RATE_LOW = 2.2;
/** Yaw rate flat-out, rad/s — wide, committed turns at speed. */
export const SKIFF_YAW_RATE_HIGH = 0.4;
/** Coasting drag when the stick is released, m/s². */
export const SKIFF_DRAG = 2.2;
/** Slow reverse cap so parking is possible, m/s. */
export const SKIFF_REVERSE_SPEED = 3;
/** Collision radius, metres. */
export const SKIFF_RADIUS = 1.4;
/** Hover height above heightAt, metres (client adds a visual bob). */
export const SKIFF_HOVER = 1.05;
/** Board within this many metres of a parked skiff. */
export const SKIFF_MOUNT_RANGE = 4;

export interface SkiffPose {
  x: number;
  z: number;
  /** Nose direction, radians (same convention as player yaw). */
  yaw: number;
  /** Signed speed along the nose; negative is reverse. */
  speed: number;
}

function approach(value: number, target: number, maxDelta: number): number {
  if (value < target) return Math.min(target, value + maxDelta);
  return Math.max(target, value - maxDelta);
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

/**
 * Advance a skiff by dt seconds of the rider's movement intent.
 *
 * `tuning` comes from the two replicated band words (skiffPerformance) and
 * defaults to a true hull. Both the server integrator and the client
 * prediction pass the same words, so a worn hull is predicted correctly
 * instead of rubber-banding. A tuning with maxSpeedFactor 0 (a dry hull)
 * accepts no throttle: the stick reads as released and the hull coasts.
 */
export function stepSkiff(
  pose: SkiffPose,
  input: InputMessage,
  dt: number,
  plan: AreaPlan,
  tuning: SkiffTuning = SKIFF_WEAR_TUNING.true,
): SkiffPose {
  let { moveX, moveZ } = input;
  if (!Number.isFinite(moveX) || !Number.isFinite(moveZ)) {
    moveX = 0;
    moveZ = 0;
  }
  const topSpeed = skiffTopSpeed(tuning);
  const accel = skiffAccel(tuning);
  const mag = topSpeed > 0 ? Math.min(1, Math.hypot(moveX, moveZ)) : 0;
  let { yaw, speed } = pose;

  if (mag > 0.05) {
    const intentDir = Math.atan2(moveX, moveZ);
    const delta = wrapAngle(intentDir - yaw);

    // Steering: chase the intent direction; authority shrinks as speed
    // grows so flat-out turns are wide and deliberate. A stick pulled
    // against the nose is a brake pedal, not a U-turn — no steering then.
    const opposing = Math.abs(delta) > Math.PI * 0.75;
    if (!opposing) {
      const authority =
        (SKIFF_YAW_RATE_LOW +
          (SKIFF_YAW_RATE_HIGH - SKIFF_YAW_RATE_LOW) * Math.min(1, Math.abs(speed) / topSpeed)) *
        tuning.yawFactor;
      yaw = wrapAngle(yaw + clamp(delta, -authority * dt, authority * dt));
    }

    // Throttle: alignment with the nose. Push forward to go, pull against
    // the nose to brake (and, once stopped, creep in reverse).
    const throttle = Math.cos(delta) * mag;
    if (throttle >= 0) {
      speed = approach(speed, throttle * topSpeed, accel * dt);
    } else if (speed > 0) {
      speed = approach(speed, 0, SKIFF_BRAKE * dt);
    } else {
      speed = approach(speed, throttle * SKIFF_REVERSE_SPEED, accel * dt);
    }
  } else {
    speed = approach(speed, 0, SKIFF_DRAG * dt);
  }

  let x = pose.x + Math.sin(yaw) * speed * dt;
  let z = pose.z + Math.cos(yaw) * speed * dt;

  for (const box of plan.obstacles) {
    const cx = clamp(x, box.minX, box.maxX);
    const cz = clamp(z, box.minZ, box.maxZ);
    const dx = x - cx;
    const dz = z - cz;
    const distSq = dx * dx + dz * dz;
    if (distSq >= SKIFF_RADIUS * SKIFF_RADIUS) continue;
    if (distSq > 1e-12) {
      const dist = Math.sqrt(distSq);
      x = cx + (dx / dist) * SKIFF_RADIUS;
      z = cz + (dz / dist) * SKIFF_RADIUS;
    } else {
      x = box.minX - SKIFF_RADIUS;
    }
    speed *= 0.4; // scraping a building sheds momentum
  }

  const maxX = plan.halfWidth - SKIFF_RADIUS;
  const maxZ = plan.halfDepth - SKIFF_RADIUS;
  x = clamp(x, -maxX, maxX);
  z = clamp(z, -maxZ, maxZ);

  return { x, z, yaw, speed };
}
